import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/shared/Common";
import { api, download, errMsg } from "@/lib/api";
import { Pick } from "@/pages/gerencia/Sales";
import { useCatalog } from "./Employees";

const ALL = "all";
const EMPTY = { template_id: "", ot_id: null, start_date: "", end_date: null, sign_date: null, extra: {} };
const LABELS = { faena: "Nombre de la faena / obra", faena_ubicacion: "Ubicación de la faena", lugar_trabajo: "Lugar de trabajo (cláusula SEGUNDO)" };

const fetchAll = async (params) => {
  let page = 1, items = [], total = 1;
  while (items.length < total) {
    const { data } = await api.get("/rrhh/employees", { params: { ...params, page, page_size: 200 } });
    items = items.concat(data.items); total = data.total; page += 1;
    if (!data.items.length) break;
  }
  return items;
};

const Filters = ({ flt, setFlt, ots, cargos, ccs }) => (
  <div className="grid sm:grid-cols-4 gap-2">
    <Input data-testid="bulk-search-input" placeholder="Buscar nombre o RUT" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} />
    <Pick testid="bulk-filter-ot" value={flt.ot} onChange={(v) => setFlt({ ...flt, ot: v })} options={[{ id: ALL, name: "Todas las OT" }, ...ots]} />
    <Pick testid="bulk-filter-cargo" value={flt.position} onChange={(v) => setFlt({ ...flt, position: v })} options={[{ id: ALL, name: "Todos los cargos" }, ...cargos]} />
    <Pick testid="bulk-filter-cc" value={flt.cost_center} onChange={(v) => setFlt({ ...flt, cost_center: v })} options={[{ id: ALL, name: "Todos los centros de costo" }, ...ccs]} />
  </div>
);

const WorkerList = ({ emps, sel, setSel }) => {
  const all = emps.length > 0 && emps.every((e) => sel.has(e.id));
  const toggle = (id) => { const s = new Set(sel); s.has(id) ? s.delete(id) : s.add(id); setSel(s); };
  const toggleAll = () => { const s = new Set(sel); emps.forEach((e) => (all ? s.delete(e.id) : s.add(e.id))); setSel(s); };
  return (
    <div className="rounded-xl border max-h-64 overflow-y-auto">
      <label className="flex items-center gap-2 px-3 py-2 border-b bg-[#f7f9f5] text-sm font-semibold sticky top-0"><Checkbox data-testid="bulk-select-all" checked={all} onCheckedChange={toggleAll} />Marcar todos ({emps.length})</label>
      {emps.map((e) => (
        <label key={e.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-[#f0f2ee] text-sm cursor-pointer hover:bg-[#f6fbf2]">
          <Checkbox data-testid={`bulk-emp-${e.id}`} checked={sel.has(e.id)} onCheckedChange={() => toggle(e.id)} /><span className="flex-1">{e.full_name}</span><span className="font-mono text-xs text-[#5b6656]">{e.rut}</span>
        </label>))}
    </div>
  );
};

const Result = ({ res }) => (
  <div className="space-y-2" data-testid="bulk-result">
    <div className="text-sm">{res.done ? <b className="text-[#1c6e00]" data-testid="bulk-done-count">{res.done} generados y descargados · </b> : <><b className="text-[#1c6e00]" data-testid="bulk-ready-count">{res.ready.length} listos</b> · </>}<b className={res.failed.length ? "text-[#b42318]" : "text-[#5b6656]"} data-testid="bulk-failed-count">{res.failed.length} omitidos (no se generan)</b></div>
    {res.failed.length > 0 && <div className="rounded-xl border border-[#f5c2c0] bg-[#fdf3f2] max-h-48 overflow-y-auto text-xs">
      {res.failed.map((f) => <div key={f.employee_id} className="px-3 py-1.5 border-b border-[#f5dcda]" data-testid={`bulk-failed-${f.employee_id}`}><b>{f.name}</b> {f.rut} — {f.reason}</div>)}
    </div>}
  </div>
);

export const BulkContractDialog = ({ open, onOpenChange, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [flt, setFlt] = useState({ q: "", ot: ALL, position: ALL, cost_center: ALL });
  const [lk, setLk] = useState({ templates: [], ots: [], ccs: [] });
  const [emps, setEmps] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const cargos = useCatalog("cargos");
  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/rrhh/contract-templates"), api.get("/rrhh/control-doc/ots").catch(() => ({ data: [] })), api.get("/gerencia/cost-centers").catch(() => ({ data: [] }))])
      .then(([t, o, c]) => setLk({ templates: t.data.items.filter((x) => x.active), ots: o.data, ccs: c.data.map((x) => ({ id: x.id, name: `${x.code} · ${x.name}` })) }));
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const p = Object.fromEntries(Object.entries({ ot: flt.ot, position: flt.position, cost_center: flt.cost_center }).filter(([, v]) => v !== ALL));
    fetchAll(p).then(setEmps).catch((e) => toast.error(errMsg(e)));
  }, [open, flt.ot, flt.position, flt.cost_center]);
  const shown = useMemo(() => { const q = flt.q.toLowerCase(); return emps.filter((e) => !q || e.full_name.toLowerCase().includes(q) || e.rut.includes(q)); }, [emps, flt.q]);
  const tpl = lk.templates.find((t) => t.id === f.template_id);
  const run = async (dry) => {
    setBusy(true);
    try {
      const { data } = await api.post("/rrhh/contracts/bulk", { ...f, employee_ids: [...sel], dry_run: dry });
      setRes(data);
      if (!dry && data.batch_id) {
        toast.success(`${data.created} ${data.created === 1 ? "documento generado" : "documentos generados"} en borrador`);
        setRes({ ...data, ready: [], done: data.created });
        setSel(new Set());
        await download(`/rrhh/contracts/batch/${data.batch_id}/docx`, {}, `contratos_${tpl.name}.docx`);
        onDone();
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const set = (patch) => { setF({ ...f, ...patch }); setRes(null); };
  const close = (o) => { if (!o) { setF(EMPTY); setSel(new Set()); setRes(null); } onOpenChange(o); };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="bulk-contract-dialog">
        <DialogHeader><DialogTitle>Generación masiva de contratos y anexos</DialogTitle></DialogHeader>
        <Field label="Plantilla *"><Pick testid="bulk-template-select" value={f.template_id} onChange={(v) => set({ template_id: v, extra: {} })} options={lk.templates.map((t) => ({ id: t.id, name: `${t.name} (${t.docx_file ? "formato Word" : t.kind})` }))} placeholder="Seleccione" /></Field>
        {tpl?.kind === "anexo" && <p className="text-xs text-[#5b6656]">Cada anexo se asocia solo al contrato vigente de cada trabajador. Los que no tengan contrato vigente se saltan.</p>}
        <Filters flt={flt} setFlt={setFlt} ots={lk.ots} cargos={cargos} ccs={lk.ccs} />
        <WorkerList emps={shown} sel={sel} setSel={(s) => { setSel(s); setRes(null); }} />
        <div className="grid sm:grid-cols-4 gap-3">
          <Field label="Inicio *"><Input data-testid="bulk-start-input" type="date" value={f.start_date} onChange={(e) => set({ start_date: e.target.value })} /></Field>
          <Field label="Término" hint={tpl?.docx_file && tpl.variables.includes("fecha_termino") ? "Obligatorio en Plazo Fijo" : "Vacío = indefinido"}><Input data-testid="bulk-end-input" type="date" value={f.end_date || ""} onChange={(e) => set({ end_date: e.target.value || null })} /></Field>
          <Field label="Fecha de firma" hint="Vacío = hoy"><Input data-testid="bulk-sign-input" type="date" value={f.sign_date || ""} onChange={(e) => set({ sign_date: e.target.value || null })} /></Field>
          <Field label="OT"><Pick testid="bulk-ot-select" value={f.ot_id || "none"} onChange={(v) => set({ ot_id: v === "none" ? null : v })} options={[{ id: "none", name: "Sin OT" }, ...lk.ots]} /></Field>
        </div>
        {tpl?.custom_vars.length > 0 && <div className="grid sm:grid-cols-2 gap-3">{tpl.custom_vars.map((v) => (
          <Field key={v} label={LABELS[v] || v} hint={LABELS[v] ? "Si elige una OT y lo deja vacío, se usa la OT" : "Igual para todos"}>
            <Input data-testid={`bulk-extra-${v}`} value={f.extra[v] || ""} onChange={(e) => set({ extra: { ...f.extra, [v]: e.target.value } })} /></Field>))}</div>}
        {res && <Result res={res} />}
        <DialogFooter className="gap-2">
          <Button data-testid="bulk-review-btn" variant="outline" className="rounded-full" disabled={busy || !f.template_id || !f.start_date || !sel.size} onClick={() => run(true)}>Revisar {sel.size} trabajadores</Button>
          <Button data-testid="bulk-generate-btn" className="rounded-full" disabled={busy || !res?.ready.length} onClick={() => run(false)}>{busy ? "Generando…" : `Generar ${res?.ready.length || 0} y descargar Word`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
